import { ITEMS, type ItemId } from "../items";
import { itemArt, loadItemArt } from "./itemArt";
import type { Tooltip } from "./tooltip";

const PACK_ICON = `
  <svg viewBox="0 0 16 16" aria-hidden="true">
    <path d="M4.2 6.6h7.6a1 1 0 0 1 1 1v5.2a1 1 0 0 1-1 1H4.2a1 1 0 0 1-1-1V7.6a1 1 0 0 1 1-1Z" />
    <path d="M6.2 6.6V5.1a1.8 1.8 0 0 1 3.6 0v1.5" />
    <path d="M3.2 9.6h9.6" />
  </svg>
`;

const COLUMNS = 6;
const ROWS = 2;
const SLOTS = COLUMNS * ROWS;
const SLOT_PIXELS = 48;
const DRAG_THRESHOLD = 7;

// exactly the name plate's colour, so the panel and its tooltips are one surface
const PANEL = "rgba(9, 8, 7, 0.9)";

const CSS = `
#inventory {
  position: fixed;
  inset: 0;
  z-index: 14;
  display: grid;
  align-items: end;
  justify-items: center;
  padding: 16px;
  /* the world stays live underneath: only the panel itself takes input */
  pointer-events: none;
  opacity: 0;
  transition: opacity 180ms ease;
  font-family: "Silkscreen", monospace;
  --slot: clamp(38px, 11vw, 44px);
}
#inventory[data-open="true"] {
  opacity: 1;
}

#inventory .panel {
  position: relative;
  padding: 14px 16px 13px;
  border-radius: 3px;
  background-color: ${PANEL};
  color: #e9e2d0;
  box-shadow: 0 8px 0 rgba(0, 0, 0, 0.42);
  transform: translateY(calc(100% + 16px));
  transition: transform 260ms cubic-bezier(0.22, 0.61, 0.36, 1);
}
#inventory[data-open="true"] .panel {
  /* only the open panel takes input, or an invisible one eats every world tap */
  pointer-events: auto;
  transform: none;
}

#inventory .head {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  height: 16px;
  margin-bottom: 8px;
}
#inventory .close {
  position: static;
  display: grid;
  place-items: center;
  width: 18px;
  height: 18px;
  padding: 0;
  border: 0;
  border-radius: 2px;
  background: rgba(233, 226, 208, 0.06);
  color: rgba(233, 226, 208, 0.5);
  font-family: "Silkscreen", monospace;
  font-size: 12px;
  line-height: 1;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}
#inventory .close:hover {
  background: rgba(233, 226, 208, 0.14);
  color: #e9e2d0;
}

#inventory[data-chest="false"] .chest-block {
  display: none;
}
#inventory .divider {
  height: 1px;
  margin: 9px 0;
  background: rgba(233, 226, 208, 0.1);
}

#inventory .grid {
  display: grid;
  grid-template-columns: repeat(${COLUMNS}, var(--slot));
  gap: 4px;
  justify-content: center;
}

#inventory .slot {
  position: relative;
  display: grid;
  place-items: center;
  width: var(--slot);
  height: var(--slot);
  border-radius: 2px;
  background: rgba(233, 226, 208, 0.05);
  box-shadow:
    inset 0 1px 0 rgba(0, 0, 0, 0.4),
    inset 0 -1px 0 rgba(233, 226, 208, 0.07);
  touch-action: none;
}
#inventory .slot[data-held="true"] {
  cursor: grab;
}
#inventory .slot[data-held="true"]:hover {
  background: rgba(233, 226, 208, 0.12);
}
#inventory .slot[data-focus="true"] {
  background: rgba(233, 226, 208, 0.18);
  box-shadow:
    inset 0 0 0 1px rgba(233, 226, 208, 0.75),
    inset 0 -1px 0 rgba(233, 226, 208, 0.2);
}
#inventory .slot[data-drag="true"] {
  opacity: 0.3;
}
#inventory .slot canvas {
  width: 100%;
  height: 100%;
  pointer-events: none;
}

#drag-ghost {
  position: fixed;
  z-index: 20;
  display: grid;
  place-items: center;
  width: 40px;
  height: 40px;
  margin: -20px 0 0 -20px;
  opacity: 0.85;
  pointer-events: none;
}
#drag-ghost canvas {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

#pack-button {
  position: fixed;
  top: max(10px, env(safe-area-inset-top));
  left: max(10px, env(safe-area-inset-left));
  z-index: 12;
  display: none;
  place-items: center;
  width: 30px;
  height: 30px;
  padding: 0;
  border: 0;
  border-radius: 2px;
  background: rgba(9, 8, 7, 0.86);
  color: #e9e2d0;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}
#pack-button[data-touch="true"] {
  display: grid;
}
#pack-button svg {
  width: 17px;
  height: 17px;
  fill: none;
  stroke: currentColor;
  stroke-width: 1.3;
  stroke-linecap: round;
  stroke-linejoin: round;
}
`;

interface Drag {
  id: ItemId | null;
  from: "chest" | "pack" | null;
  x: number;
  y: number;
  startX: number;
  startY: number;
  active: boolean;
  ghost: HTMLDivElement | null;
  source: HTMLDivElement | null;
}

export class Inventory {
  private readonly root: HTMLDivElement;
  private readonly panel: HTMLDivElement;
  private readonly button: HTMLButtonElement;
  private focusRow = 0;
  private focusCol = 0;
  private readonly slots: HTMLDivElement[] = [];
  private readonly chestSlots: HTMLDivElement[] = [];
  private tintShadow = 0x191c23;
  private tintHighlight = 0xf7f5ef;
  private held: ItemId[] = [];
  private chestItems: ItemId[] = [];
  private open = false;
  private onToggle: (() => void) | null = null;
  private onStore: ((id: ItemId) => void) | null = null;
  private onTake: ((id: ItemId) => void) | null = null;
  private dragSource: HTMLDivElement | null = null;
  private readonly drag: Drag = {
    id: null,
    from: null,
    x: 0,
    y: 0,
    startX: 0,
    startY: 0,
    active: false,
    ghost: null,
    source: null,
  };

  constructor(private readonly tip: Tooltip) {
    const style = document.createElement("style");
    style.textContent = CSS;
    document.head.appendChild(style);

    // the art is tinted on a canvas, so the panel has to redraw once it lands
    void loadItemArt(Object.keys(ITEMS) as ItemId[]).then(() => this.refresh());

    this.button = document.createElement("button");
    this.button.id = "pack-button";
    this.button.type = "button";
    this.button.innerHTML = PACK_ICON;
    this.button.dataset.touch = window.matchMedia?.("(hover: none) and (pointer: coarse)").matches
      ? "true"
      : "false";
    this.button.addEventListener("click", (event) => {
      event.stopPropagation();
      this.toggle();
    });

    this.root = document.createElement("div");
    this.root.id = "inventory";
    this.root.dataset.open = "false";
    this.root.dataset.chest = "false";

    this.panel = document.createElement("div");
    this.panel.className = "panel";

    const close = document.createElement("button");
    close.className = "close";
    close.type = "button";
    close.setAttribute("aria-label", "close");
    close.textContent = "X";
    close.addEventListener("click", (event) => {
      event.stopPropagation();
      this.close();
    });

    const chestGrid = this.buildGrid(this.chestSlots, "chest");
    const divider = document.createElement("div");
    divider.className = "divider";
    const packGrid = this.buildGrid(this.slots, "pack");

    const chestBlock = document.createElement("div");
    chestBlock.className = "chest-block";
    chestBlock.append(chestGrid, divider);

    const head = document.createElement("div");
    head.className = "head";
    head.appendChild(close);

    this.panel.append(head, chestBlock, packGrid);
    this.root.appendChild(this.panel);
    document.body.append(this.button, this.root);

    window.addEventListener("pointermove", this.onPointerMove);
    window.addEventListener("pointerup", this.onPointerUp);
    window.addEventListener("pointercancel", this.onPointerUp);
  }

  get isOpen(): boolean {
    return this.open;
  }

  /** distance from the bottom of the screen to the panel's top edge */
  topInset(): number {
    if (!this.open) return 0;
    const rect = this.panel.getBoundingClientRect();
    return Math.max(0, window.innerHeight - rect.top);
  }

  /** the world's current tints; icons follow them so they belong to the hour */
  setTints(shadow: number, highlight: number): void {
    if (shadow === this.tintShadow && highlight === this.tintHighlight) return;
    this.tintShadow = shadow;
    this.tintHighlight = highlight;
    if (!this.open) return;
    this.panel.dataset.pack = "";
    this.panel.dataset.chestRow = "";
    this.set(this.held);
    this.setChest(this.root.dataset.chest === "true" ? this.chestItems : null);
  }

  onToggleRequest(handler: () => void): void {
    this.onToggle = handler;
  }

  onStoreRequest(handler: (id: ItemId) => void): void {
    this.onStore = handler;
  }

  onTakeRequest(handler: (id: ItemId) => void): void {
    this.onTake = handler;
  }

  set(held: ItemId[]): void {
    const signature = held.join("|");
    if (this.panel.dataset.pack === signature) return;
    this.panel.dataset.pack = signature;
    this.held = held;
    this.paint(this.slots, held);
    this.paintFocus();
  }

  /** null hides the chest row; an empty array shows an open, empty chest */
  setChest(ids: ItemId[] | null): void {
    this.chestItems = ids ?? [];
    const signature = ids === null ? "none" : ids.join("|");
    if (this.panel.dataset.chestRow === signature) return;
    this.panel.dataset.chestRow = signature;
    this.root.dataset.chest = ids === null ? "false" : "true";
    this.paint(this.chestSlots, this.chestItems);
    this.paintFocus();
  }

  /**
   * Controller cursor: the d-pad walks the slots, A takes out of the chest or
   * puts back into it. Two fixed containers means a press moves the item to the
   * other one, which is the same result as pick-up-then-place with less state.
   */
  moveFocus(dx: number, dy: number): void {
    const rows = this.rows();
    this.focusRow = Math.min(rows.length - 1, Math.max(0, this.focusRow + dy));
    this.focusCol = Math.min(COLUMNS - 1, Math.max(0, this.focusCol + dx));
    this.paintFocus();
  }

  confirmFocus(): void {
    const slot = this.focusSlot();
    const id = slot?.dataset.item as ItemId | undefined;
    if (!id || !slot) return;
    const row = this.rows()[this.focusRow];
    if (row?.from === "chest") this.onTake?.(id);
    // you can only put something into a container that is actually open
    else if (row && this.root.dataset.chest === "true") this.onStore?.(id);
  }

  private rows(): Array<{ from: "chest" | "pack"; start: number }> {
    const rows: Array<{ from: "chest" | "pack"; start: number }> = [];
    if (this.root.dataset.chest === "true") rows.push({ from: "chest", start: 0 });
    rows.push({ from: "pack", start: 0 });
    rows.push({ from: "pack", start: COLUMNS });
    return rows;
  }

  private focusSlot(): HTMLDivElement | null {
    const rows = this.rows();
    const row = rows[Math.min(this.focusRow, rows.length - 1)];
    if (!row) return null;
    const slots = row.from === "chest" ? this.chestSlots : this.slots;
    return slots[row.start + this.focusCol] ?? null;
  }

  private paintFocus(): void {
    for (const slot of [...this.chestSlots, ...this.slots]) slot.dataset.focus = "false";
    if (!this.open) return;
    const slot = this.focusSlot();
    if (!slot) return;
    slot.dataset.focus = "true";
    const id = slot.dataset.item as ItemId | undefined;
    if (id && slot) this.showName(id, slot);
    else this.tip.hide();
  }

  toggle(): void {
    if (this.open) this.close();
    else this.show();
  }

  show(): void {
    if (this.open) return;
    this.open = true;
    this.root.dataset.open = "true";
    this.focusRow = 0;
    this.focusCol = 0;
    this.paintFocus();
    this.onToggle?.();
  }

  close(): void {
    this.clearDrag();
    if (!this.open) return;
    this.open = false;
    this.root.dataset.open = "false";
    this.tip.hide();
    this.onToggle?.();
  }

  picture(id: ItemId, size: number): HTMLCanvasElement {
    const canvas = document.createElement("canvas");
    const art = itemArt(id, size, this.tintShadow, this.tintHighlight);
    if (!art) return canvas;
    canvas.width = art.width;
    canvas.height = art.height;
    canvas.getContext("2d")?.drawImage(art, 0, 0);
    return canvas;
  }

  /** the art loads asynchronously; repaint everything once it has */
  private refresh(): void {
    this.panel.dataset.pack = "";
    this.panel.dataset.chestRow = "";
    this.set(this.held);
    this.setChest(this.root.dataset.chest === "true" ? this.chestItems : null);
  }

  private buildGrid(target: HTMLDivElement[], from: "chest" | "pack"): HTMLDivElement {
    const grid = document.createElement("div");
    grid.className = from === "chest" ? "grid chest-grid" : "grid pack-grid";
    const count = from === "chest" ? COLUMNS : SLOTS;
    for (let i = 0; i < count; i++) {
      const slot = document.createElement("div");
      slot.className = "slot";
      slot.dataset.held = "false";
      slot.addEventListener("pointerenter", (event) => {
        if (event.pointerType === "mouse") this.label(slot);
      });
      // a finger has no hover, so a tap names the item instead
      slot.addEventListener("pointerleave", () => this.tip.hide());
      slot.addEventListener("pointerdown", (event) => this.beginDrag(event, slot, from, i));
      target.push(slot);
      grid.appendChild(slot);
    }
    return grid;
  }

  private paint(slots: HTMLDivElement[], items: ItemId[]): void {
    slots.forEach((slot, index) => {
      const id = items[index];
      if (!id) {
        slot.replaceChildren();
        slot.dataset.held = "false";
        delete slot.dataset.item;
        return;
      }
      slot.replaceChildren(this.picture(id, SLOT_PIXELS));
      slot.dataset.held = "true";
      slot.dataset.item = id;
    });
  }

  private label(slot: HTMLDivElement | null): void {
    const id = slot?.dataset.item as ItemId | undefined;
    if (!slot || !id) {
      this.tip.hide();
      return;
    }
    this.showName(id, slot);
  }

  /** one place decides where a name goes, so it is always clamped to the panel */
  private showName(id: ItemId, slot: HTMLDivElement): void {
    const panel = this.panel.getBoundingClientRect();
    this.tip.showAbove(ITEMS[id].name, slot.getBoundingClientRect(), {
      left: panel.left,
      right: panel.right,
    });
  }


  private beginDrag(
    event: PointerEvent,
    source: HTMLDivElement,
    from: "chest" | "pack",
    index: number,
  ): void {
    const id = (from === "chest" ? this.chestItems : this.held)[index];
    if (!id) return;
    this.drag.id = id;
    this.drag.from = from;
    this.drag.x = event.clientX;
    this.drag.y = event.clientY;
    this.drag.startX = event.clientX;
    this.drag.startY = event.clientY;
    this.drag.active = false;
    this.drag.source = source;
    this.dragSource = source;
  }

  private readonly onPointerMove = (event: PointerEvent): void => {
    if (!this.drag.id) return;
    this.drag.x = event.clientX;
    this.drag.y = event.clientY;
    if (!this.drag.active) {
      const moved = Math.hypot(event.clientX - this.drag.startX, event.clientY - this.drag.startY);
      if (moved < DRAG_THRESHOLD) return;
      this.drag.active = true;
      if (this.drag.source) this.drag.source.dataset.drag = "true";
      this.makeGhost();
    }
    if (this.drag.ghost) {
      this.drag.ghost.style.left = `${event.clientX}px`;
      this.drag.ghost.style.top = `${event.clientY}px`;
    }
  };

  private readonly onPointerUp = (event: PointerEvent): void => {
    const { id, from, active } = this.drag;
    if (!id || !from) return;
    const x = event.clientX;
    const y = event.clientY;
    this.clearDrag();
    if (!active) {
      if (from === "chest") this.onTake?.(id);
      else this.label(this.dragSource);
      return;
    }

    const under = document.elementFromPoint(x, y);
    const slot = under instanceof Element ? under.closest(".slot") : null;
    const overChest = slot !== null && slot.closest(".chest-grid") !== null;
    const overPack = slot !== null && slot.closest(".pack-grid") !== null;
    if (from === "chest" && overPack) this.onTake?.(id);
    else if (from === "pack" && overChest) this.onStore?.(id);
    else if (from === "pack" && overPack) this.label(this.dragSource);
  };

  private clearDrag(): void {
    if (this.drag.source) delete this.drag.source.dataset.drag;
    this.drag.ghost?.remove();
    this.drag.ghost = null;
    this.drag.source = null;
    this.drag.id = null;
    this.drag.from = null;
    this.drag.active = false;
  }

  private makeGhost(): void {
    const id = this.drag.id;
    if (!id) return;
    const ghost = document.createElement("div");
    ghost.id = "drag-ghost";
    ghost.appendChild(this.picture(id, SLOT_PIXELS));
    ghost.style.left = `${this.drag.x}px`;
    ghost.style.top = `${this.drag.y}px`;
    document.body.appendChild(ghost);
    this.drag.ghost = ghost;
  }
}
